import { Link, useParams } from 'react-router-dom';
import { findService } from '@/data/services';
import { Seo, serviceJsonLd, breadcrumbJsonLd } from '@/lib/seo';
import { Hero } from '@/components/sections/Hero';
import { CTABand } from '@/components/sections/CTABand';
import { GuaranteeStrip } from '@/components/sections/GuaranteeStrip';
import { QuoteForm } from '@/components/forms/QuoteForm';
import { ArrowRight, ChevronRight } from '@/components/ui/Icons';
import { company } from '@/data/company';
import NotFound from '@/pages/NotFound';

export default function ServicePage() {
  const { service: slug } = useParams();
  const service = slug ? findService(slug) : undefined;
  if (!service) return <NotFound />;

  const path = `/${service.slug}`;
  const heroImage = `/images/services/${service.slug}.jpg`;
  const description =
    `${service.short} Free site surveys, no call-out charges, all work guaranteed by ${company.legalName} across ${company.serviceCoverage}.`;

  const subOfferings = service.subservices.map((sub) => ({
    title: sub.title,
    url: `/${service.slug}/${sub.slug}`,
    description: sub.short,
  }));

  return (
    <>
      <Seo
        title={service.title}
        description={description}
        path={path}
        image={heroImage}
        imageAlt={`${service.title} by ${company.legalName}`}
        keywords={`${service.navLabel}, ${service.navLabel} Essex, ${service.navLabel} London, ${service.navLabel} Suffolk, ${company.shortName}`}
        jsonLd={[
          serviceJsonLd(service.title, path, description, {
            image: heroImage,
            subservices: subOfferings,
          }),
          breadcrumbJsonLd([
            { name: 'Home', path: '/' },
            { name: service.navLabel, path },
          ]),
        ]}
      />

      <Hero
        eyebrow={service.navLabel}
        title={service.hero}
        subtitle={service.short}
        imageSrc={heroImage}
      />

      <nav aria-label="Breadcrumb" className="bg-paper-50">
        <div className="container-page py-3 text-sm text-ink-700">
          <ol className="flex flex-wrap items-center gap-1">
            <li><Link to="/" className="hover:text-navy-700">Home</Link></li>
            <li className="px-1 text-gold-500"><ChevronRight className="h-3 w-3" /></li>
            <li className="font-semibold text-navy-800">{service.navLabel}</li>
          </ol>
        </div>
      </nav>

      <GuaranteeStrip />

      <section className="container-page py-16 sm:py-20">
        <div className="grid gap-10 lg:grid-cols-[1.4fr_1fr]">
          <div className="prose-bliss">
            <h2 className="text-3xl">{service.hero}</h2>
            {service.intro.map((p) => <p key={p}>{p}</p>)}
            {service.bullets && service.bullets.length > 0 && (
              <>
                <h3>Why customers choose us</h3>
                <ul className="chevron-list space-y-2">
                  {service.bullets.map((b) => <li key={b}>{b}</li>)}
                </ul>
              </>
            )}
            <h3>Where we work</h3>
            <p>
              We deliver {service.navLabel.toLowerCase()} across {company.serviceCoverage}, including
              {' '}{company.serviceTowns.join(', ')}. Free site surveys, honest written quotes,
              no hidden extras.
            </p>
          </div>
          <div className="self-start">
            <QuoteForm defaultService={service.slug} />
          </div>
        </div>
      </section>

      {service.subservices.length > 0 && (
        <section className="bg-paper-50 py-16 sm:py-20">
          <div className="container-page">
            <header className="mb-10 max-w-2xl">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-gold-600">
                {service.navLabel}
              </p>
              <h2 className="mt-2 font-display text-3xl font-semibold text-navy-900 sm:text-4xl">
                Specific services we offer
              </h2>
            </header>

            <ul className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {service.subservices.map((sub) => (
                <li key={sub.slug}>
                  <Link
                    to={`/${service.slug}/${sub.slug}`}
                    className="group flex h-full flex-col rounded-lg border border-paper-100 bg-paper-0 p-5 shadow-card transition hover:-translate-y-1 hover:border-gold-300"
                  >
                    <h3 className="font-display text-lg font-semibold text-navy-900">{sub.title}</h3>
                    <p className="mt-2 text-sm text-ink-700">{sub.short}</p>
                    <span className="mt-auto inline-flex items-center gap-1 pt-4 text-sm font-semibold text-gold-700 group-hover:text-gold-600">
                      Read more <ArrowRight className="h-4 w-4" />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      <CTABand />
    </>
  );
}
